import type { EffectsProp } from 'remotion';
import { lut } from '@remotion/effects/lut';
import { colorCorrection } from '@remotion/effects/color-correction';
import { blur } from '@remotion/effects/blur';
import { vignette } from '@remotion/effects/vignette';
import { noise } from '@remotion/effects/noise';
import { grayscale } from '@remotion/effects/grayscale';
import { EFFECT_CONTROLS, hasVideoEffects, type VideoEffects } from './types';
import { lutCubeContent } from './cube';
import { getPresetLutData } from './presets';
import { keyWithFrameAspect, takeFrameAspect, visibleFrameWidth, withFrameAspect } from './library/pixelScaled';

/** Same definitions for native preview and Remotion export; neutral means no GPU work.
 *  `frameAspect` (composition width / height) scales the blur to the visible
 *  frame, like timeline sections; omit it to keep the source-width scaling. */
export function buildVideoEffects(value: VideoEffects, width: number, frameAspect?: number): EffectsProp {
  if (!hasVideoEffects(value)) return [];
  const effects: Array<EffectsProp[number]> = [];
  if (EFFECT_CONTROLS.some(c => c.group === 'adjust' && value[c.key] !== 0)) effects.push(colorCorrection({
    exposure: value.exposure, contrast: 1 + value.contrast / 100, saturation: 1 + value.saturation / 100,
    temperature: value.temperature / 100, tint: value.tint / 100, shadows: value.shadows / 100,
    highlights: value.highlights / 100, vibrance: value.vibrance / 100,
  }));
  if (value.lut && value.lut.strength > 0) {
    const data = value.lut.preset === 'custom' ? value.lut.data! : getPresetLutData(value.lut.preset);
    effects.push(lut({ content: lutCubeContent(data, value.lut.strength) }));
  }
  if (value.blur > 0) {
    effects.push(withFrameAspect(blur({ radius: value.blur / 100 * 12 * width / 1080 }), scaledBlurDefinition, frameAspect));
  }
  if (value.vignette > 0) effects.push(vignette({ amount: value.vignette / 100 }));
  if (value.grain > 0) effects.push(noise({ amount: value.grain / 100 * 0.3, seed: 42 }));
  if (value.grayscale > 0) effects.push(grayscale({ amount: value.grayscale / 100 }));
  return effects;
}

// Scale blur to the decoded source's VISIBLE width (see pixelScaled) in BOTH
// adapters, independent of compressed/HD tiers or export resolution. Base look
// and section blur share it, so equal amounts match. Keep definition identity stable.
const blurDefinition = blur({ radius: 0 }).definition;
export const scaledBlurDefinition: typeof blurDefinition = {
  ...blurDefinition,
  calculateKey: keyWithFrameAspect(blurDefinition.calculateKey),
  apply: args => {
    const { frameAspect, params } = takeFrameAspect(args.params);
    return blurDefinition.apply({ ...args, params: {
      ...params,
      radius: (params as { radius: number }).radius * visibleFrameWidth(args.width, args.height, frameAspect) / 1080,
    } });
  },
};
